import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IListaCEP } from '../../../models';
import { IFormError, IFormProps } from '../../utils';

interface ICreateListaCEPFormFields {
    nome: string;
    idUsuario: number;
}

interface IEditListaCEPFormFields {
    idVelesGeoListaCEP: number;
    nome: string;
    ceps: string[]
    ativa: boolean;
}

const createListaCEPFormFieldsInitialState: ICreateListaCEPFormFields = {
    nome: '',
    idUsuario: 0
}

const editListaCEPFormFieldsInitialState: IEditListaCEPFormFields = {
    idVelesGeoListaCEP: 0,
    nome: '',
    ativa: false,
    ceps: []
}

interface IListaCEPState {
    matchCode: {
        selectedColumn: string,
        distinctValues: number,
        validValues: number
    },
    fileData: {
        name: string,
        size: string,
        columns: string[],
        type: string,
        data: any[],
        validData?: any[],
        invalidData?: any[]
    },
    searchTerm: string
    listasCEP: IListaCEP[]
    selectedCepList?: IListaCEP
    fileListData: {
        fileListData: string[]
        fileListTableValues: {
            fileListValidData: string[]
            fileListInvalidData: string[]
            fileListRepeatedData: string[]
        }
    }
    createListaCEPForm: IFormProps<ICreateListaCEPFormFields>
    openCreateListaCEP: boolean
    editListaCEPForm: IFormProps<IEditListaCEPFormFields>
    openEditListaCEP: boolean
    openImportFileListaCEP: boolean
    openPreviewListaCEP: boolean
    openEditCEP: boolean
}

const initialState: IListaCEPState = {
    fileListData: {
        fileListData: [],
        fileListTableValues: {
            fileListInvalidData: [],
            fileListRepeatedData: [],
            fileListValidData: []
        }
    },
    matchCode: {
        selectedColumn: '',
        distinctValues: 0,
        validValues: 0
    },
    fileData: {
        name: '',
        size: '',
        columns: [],
        type: '',
        data: []
    },
    listasCEP: [],
    searchTerm: '',
    createListaCEPForm: {
        errors: [],
        fields: createListaCEPFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    openCreateListaCEP: false,
    editListaCEPForm: {
        errors: [],
        fields: editListaCEPFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    openEditListaCEP: false,
    openImportFileListaCEP: false,
    openPreviewListaCEP: false,
    openEditCEP: false
};

export const validateCreateListaCEPForm = (fields: ICreateListaCEPFormFields): IFormError<keyof ICreateListaCEPFormFields>[] => {
    const errors: IFormError<keyof ICreateListaCEPFormFields>[] = [];

    if (!fields.nome) {
        errors.push({
            key: 'nome',
            message: 'Nome é obrigatório',
        })
    } else {
        if (fields.nome.length === 0 || fields.nome.replace('  ', ' ').trim().length === 0) {
            errors.push({
                key: 'nome',
                message: 'O nome da lista não pode ficar em branco'
            })
        }
    }

    return errors;
}

export const validateEditListaCEPForm = (fields: IEditListaCEPFormFields): IFormError<keyof IEditListaCEPFormFields>[] => {
    const errors: IFormError<keyof IEditListaCEPFormFields>[] = [];

    if (!fields.nome) {
        errors.push({
            key: 'nome',
            message: 'Nome é obrigatório',
        });
    } else {
        if (fields.nome.length === 0 || fields.nome.replace('  ', ' ').trim().length === 0) {
            errors.push({
                key: 'nome',
                message: 'O nome do projeto não pode ficar em branco'
            })
        }
    }

    return errors;
}

const listaCEPSlice = createSlice({
    name: 'listaCEP',
    initialState,
    reducers: {
        resetListaCEPState(state) {
            state = initialState
        },
        setListasCEPState(state, action: PayloadAction<{ listasCEP: IListaCEP[] }>) {
            state.listasCEP = action.payload.listasCEP
        },
        resetListasCEPState(state) {
            state.listasCEP = initialState.listasCEP
        },
        setSelectedListasCEPState(state, action: PayloadAction<{ listaCEP: IListaCEP }>) {
            state.selectedCepList = action.payload.listaCEP
        },
        // Create Lista CEP
        setCreateListaCEPNomeState(state, action: PayloadAction<{ nome: string }>) {
            state.createListaCEPForm.fields.nome = action.payload.nome
        },
        setCreateListaCEPIdUsuarioState(state, action: PayloadAction<{ idUsuario: number }>) {
            state.createListaCEPForm.fields.idUsuario = action.payload.idUsuario
        },
        setCreateListaCEPFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
            state.createListaCEPForm.touched = action.payload.touched;
        },
        setCreateListaCEPFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof ICreateListaCEPFormFields>[] }>) {
            state.createListaCEPForm.errors = action.payload.errors;
        },
        setCreateListaCEPFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
            state.createListaCEPForm.delivered = action.payload.delivered;
        },
        resetCreateListaCEPFormState(state) {
            state.createListaCEPForm = initialState.createListaCEPForm
        },
        // Edit Lista CEP
        setEditListaCEPIdVelesGeoListaCEPState(state, action: PayloadAction<{ idVelesGeoListaCEP: number }>) {
            state.editListaCEPForm.fields.idVelesGeoListaCEP = action.payload.idVelesGeoListaCEP
        },
        setEditListaCEPNomeState(state, action: PayloadAction<{ nome: string }>) {
            state.editListaCEPForm.fields.nome = action.payload.nome
        },
        setEditListaCEPAtivaState(state, action: PayloadAction<{ ativa: boolean }>) {
            state.editListaCEPForm.fields.ativa = action.payload.ativa
        },
        setCEPsEditListaCEPState(state, action: PayloadAction<{ ceps: string[] }>) {
            state.editListaCEPForm.fields.ceps = action.payload.ceps
        },
        setAddCEPListEditListaCEPState(state, action: PayloadAction<{ ceps: string[] }>) {
            state.editListaCEPForm.fields.ceps = [...state.editListaCEPForm.fields.ceps, ...action.payload.ceps]
        },
        setEditListaCEPAddCEPState(state, action: PayloadAction<{ cep: string }>) {
            if (state.editListaCEPForm.fields.ceps.includes(action.payload.cep)) {
                state.editListaCEPForm.fields.ceps.push(action.payload.cep);
            }
            state.editListaCEPForm.fields.ceps.push(action.payload.cep)
        },
        setEditListaCEPRemoveCEPState(state, action: PayloadAction<{ cep: string }>) {
            state.editListaCEPForm.fields.ceps = state.editListaCEPForm.fields.ceps.filter(item => item !== action.payload.cep)
        },
        setEditListaCEPEditCEPState(state, action: PayloadAction<{
            oldCEP: string,
            newCEP: string
        }>) {
            if (state.editListaCEPForm.fields.ceps.includes(action.payload.newCEP)) {
                return
            }

            state.editListaCEPForm.fields.ceps = state.editListaCEPForm.fields.ceps.map(item => item === action.payload.oldCEP ? action.payload.newCEP : item)
        },
        setEditListaCEPFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
            state.editListaCEPForm.touched = action.payload.touched;
        },
        setEditListaCEPFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof IEditListaCEPFormFields>[] }>) {
            state.editListaCEPForm.errors = action.payload.errors;
        },
        setEditListaCEPFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
            state.editListaCEPForm.delivered = action.payload.delivered;
        },
        resetEditListaCEPFormState(state) {
            state.editListaCEPForm = initialState.editListaCEPForm
        },
        setListaCEPFileDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListData = action.payload.value
        },
        setListaCEPFileValidDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListValidData = action.payload.value
        },
        setListaCEPFileRepeatedDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListRepeatedData = action.payload.value
        },
        setListaCEPFileInvalidDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListInvalidData = action.payload.value
        },
        setListaCEPSelectedColumnState(state, action: PayloadAction<{
            selectedColumn: string
        }>) {
            state.matchCode.selectedColumn = action.payload.selectedColumn
        },
        setListaCEPDistinctValuesState(state, action: PayloadAction<{
            distinctValues: number
        }>) {
            state.matchCode.distinctValues = action.payload.distinctValues
        },
        setListaCEPValidValuesState(state, action: PayloadAction<{
            validValues: number
        }>) {
            state.matchCode.validValues = action.payload.validValues
        },
        setListaCEPFileDataState(state, action: PayloadAction<{
            fileData: {
                name: string,
                size: string,
                columns: string[],
                type: string,
                data: any[],
                validData?: any[],
                invalidData?: any[]
            }
        }>) {
            state.fileData = action.payload.fileData
            
        },
        resetListaCEPFileDataState(state) {
            state.fileData = initialState.fileData
            state.matchCode = initialState.matchCode
            state.fileListData = initialState.fileListData
        },
        setOpenCreateListaCEPState(state, action: PayloadAction<boolean>) {
            state.openCreateListaCEP = action.payload
        },
        setOpenEditListaCEPState(state, action: PayloadAction<boolean>) {
            state.openEditListaCEP = action.payload
        },
        setOpenImportFileCEPState(state, action: PayloadAction<boolean>) {
            state.openImportFileListaCEP = action.payload
        },
        setOpenPreviewListaCEPState(state, action: PayloadAction<boolean>) {
            state.openPreviewListaCEP = action.payload
        },
        setOpenEditCEPState(state, action: PayloadAction<boolean>) {
            state.openEditCEP = action.payload
        }
    }
});

export const {
    resetCreateListaCEPFormState,
    resetEditListaCEPFormState,
    resetListasCEPState,
    resetListaCEPFileDataState,
    resetListaCEPState,
    setAddCEPListEditListaCEPState,
    setCEPsEditListaCEPState,
    setCreateListaCEPFormDeliveredState,
    setCreateListaCEPFormErrorsState,
    setCreateListaCEPFormTouchedState,
    setCreateListaCEPIdUsuarioState,
    setCreateListaCEPNomeState,
    setEditListaCEPAddCEPState,
    setEditListaCEPAtivaState,
    setEditListaCEPEditCEPState,
    setEditListaCEPFormDeliveredState,
    setEditListaCEPFormErrorsState,
    setEditListaCEPFormTouchedState,
    setEditListaCEPIdVelesGeoListaCEPState,
    setEditListaCEPNomeState,
    setEditListaCEPRemoveCEPState,
    setListaCEPDistinctValuesState,
    setListaCEPFileDataListState,
    setListaCEPFileDataState,
    setListaCEPFileInvalidDataListState,
    setListaCEPFileRepeatedDataListState,
    setListaCEPFileValidDataListState,
    setListaCEPSelectedColumnState,
    setListaCEPValidValuesState,
    setListasCEPState,
    setSelectedListasCEPState,
    setOpenCreateListaCEPState,
    setOpenEditListaCEPState,
    setOpenImportFileCEPState,
    setOpenPreviewListaCEPState,
    setOpenEditCEPState
} = listaCEPSlice.actions;

export const listaCEPReducer = listaCEPSlice.reducer;